/**
 * IdpList — admin view of every IDP in the workspace.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Target, Plus, User, Calendar } from "lucide-react";

export default function IdpList() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", title: "", target_role: "", timeline_end: "" });

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const [{ data }, { data: m }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/idp/plans`),
        api.get(`/workspaces/${activeWorkspaceId}/members`),
      ]);
      setRows(Array.isArray(data) ? data : []);
      setMembers(Array.isArray(m) ? m : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.user_id || !form.title) return toast.error("User and title required");
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/idp/plans`, form);
      setOpen(false);
      navigate(`/app/idp/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Individual Development Plans"
        description="Personal growth roadmaps for every employee"
        actions={
          <Button className="rounded-none h-9" onClick={() => setOpen(true)} data-testid="idp-new">
            <Plus className="w-4 h-4 mr-1" /> New IDP
          </Button>
        }
      />
      {rows.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center">
          <Target className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No IDPs yet.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((p) => (
          <div key={p.id} className="border border-border p-4 hover:border-primary cursor-pointer" onClick={() => navigate(`/app/idp/${p.id}`)} data-testid={`idp-card-${p.id}`}>
            <div className="flex items-start gap-2">
              <Target className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{p.title}</div>
                <div className="text-[10px] uppercase text-muted-foreground mt-0.5"><User className="w-3 h-3 inline" /> {p.user_name} · {p.status}</div>
              </div>
            </div>
            {p.target_role && <p className="text-xs text-muted-foreground mt-2">Target: {p.target_role}</p>}
            <div className="mt-3">
              <div className="w-full h-2 bg-muted rounded-none">
                <div className="h-2 bg-emerald-500" style={{ width: `${p.progress_percent || 0}%` }} />
              </div>
              <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                <span>{(p.goals || []).length} goals</span>
                <span>· {(p.courses || []).length} courses</span>
                <span className="ml-auto">{p.progress_percent || 0}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="idp-new-dialog">
          <DialogHeader><DialogTitle>New IDP</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs mb-1 block">Employee *</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger className="h-9 rounded-none" data-testid="idp-new-user"><SelectValue placeholder="Select an employee" /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>{m.name || m.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. FY26 growth plan" className="h-9 rounded-none" data-testid="idp-new-title" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Target role</Label>
              <Input value={form.target_role} onChange={(e) => setForm({ ...form, target_role: e.target.value })} placeholder="e.g. Senior Account Manager" className="h-9 rounded-none" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Target completion date</Label>
              <Input type="date" value={form.timeline_end} onChange={(e) => setForm({ ...form, timeline_end: e.target.value })} className="h-9 rounded-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={create} data-testid="idp-new-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
